const CONFIG = {
  // 当前环境：'production' | 'test'
  env: getApiEnvByTheme(),

  // 接口根地址映射
  urls: {
    proxy: {
      production: 'https://www.kemimoto.com/apps/kemimoto-proxy',
      // test: 'http://172.18.50.105:8086',
      test: 'https://www.kemimoto.com/apps/kemimoto-proxy-test',
    },
    // 普通 API 类接口
    api: {
      production: 'https://api.kemimoto.com',
      test: 'https://apitest.kemimoto.com',
    },
  },
};

function getApiEnvByTheme() {
  if (typeof window === 'undefined') {
    return 'production';
  }

  if (window.KM_THEME_ENV === 'production' || window.KM_THEME_ENV === 'test') {
    return window.KM_THEME_ENV;
  }

  const themeRole = window.KM_THEME_ROLE || (window.Shopify && window.Shopify.theme && window.Shopify.theme.role);
  return themeRole === 'main' || themeRole === 'live' ? 'production' : 'test';
}


class ApiClient {
  constructor(config) {
    this.env = config.env;
    this.urls = config.urls;
  }

  /**
   * 设置/切换当前环境 ('production' | 'test')
   */
  setEnv(env) {
    if (['production', 'test'].includes(env)) {
      this.env = env;
      console.log(`[ApiClient] 环境已切换为: ${env}`);
    } else {
      console.error(`[ApiClient] 无效的环境设置: ${env}`);
    }
  }

  /**
   * 获取完整 URL
   * @param {string} endpoint - 接口路径，例如 "/user/info" 或 "user/info"
   * @param {boolean} isProxy - 是否使用 proxy 域
   */
  _buildUrl(endpoint, isProxy = false) {
    if (/^https?:\/\//i.test(endpoint)) {
      return endpoint;
    }

    const domainType = isProxy ? 'proxy' : 'api';
    const baseUrl = this.urls[domainType][this.env];

    const formattedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `${baseUrl}${formattedEndpoint}`;
  }

  /**
   * 基础 Fetch 封装
   * @param {Object} options
   * @param {string} options.endpoint - 接口相对路径
   * @param {string} options.method - HTTP 动词 GET|POST|PUT|DELETE
   * @param {Object} [options.data] - Body 数据 (POST/PUT/DELETE)
   * @param {Object} [options.params] - Query 参数 (GET)
   * @param {boolean} [options.isProxy] - 是否走 proxy 域名
   * @param {Object} [options.headers] - 自定义请求头
   */
  async _request({ endpoint, method = 'GET', data = null, params = null, isProxy = false, headers = {}, options = {} }) {
    let url = this._buildUrl(endpoint, isProxy);
    const {
      responseType = 'json',
      skipDefaultHeaders = false,
      ...nativeOptions
    } = options || {};

    if (params && typeof params === 'object') {
      const queryString = new URLSearchParams(params).toString();
      if (queryString) {
        url += (url.includes('?') ? '&' : '?') + queryString;
      }
    }

    const defaultHeaders = skipDefaultHeaders
      ? { ...headers }
      : {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...headers,
        };

    const fetchOptions = {
      ...nativeOptions,
      method: method.toUpperCase(),
      headers: defaultHeaders,
    };

    // 4. 处理 Body 数据
    if (data && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(fetchOptions.method)) {
      fetchOptions.body = typeof data === 'string' ? data : JSON.stringify(data);
    }

    try {
      const response = await fetch(url, fetchOptions);

      // 处理 HTTP 状态码非 2xx 的错误
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || `HTTP Error: ${response.status}`);
        error.status = response.status;
        error.data = errorData;
        throw error;
      }

      if (response.status === 204) {
        return null;
      }

      if (responseType === 'raw') {
        return response;
      }
      if (responseType === 'text') {
        return await response.text();
      }

      return await response.json();
    } catch (error) {
      if (!error || error.name !== 'AbortError') {
        console.error(`[API Error] [${method}] ${url}:`, error);
      }
      throw error; // 抛出错误
    }
  }



  /**
   * GET 请求
   * @param {string} endpoint 接口路径
   * @param {Object} params URL参数
   * @param {boolean} isProxy 是否走 Proxy 接口 (默认 false)
   * @param {Object} headers 自定义请求头
   */
  get(endpoint, params = null, isProxy = false, headers = {}, options = {}) {
    return this._request({ endpoint, method: 'GET', params, isProxy, headers, options });
  }

  /**
   * POST 请求
   * @param {string} endpoint 接口路径
   * @param {Object} data 请求 Body 数据
   * @param {boolean} isProxy 是否走 Proxy 接口 (默认 false)
   * @param {Object} headers 自定义请求头
   */
  post(endpoint, data = null, isProxy = false, headers = {}, options = {}) {
    return this._request({ endpoint, method: 'POST', data, isProxy, headers, options });
  }

  /**
   * PUT 请求
   */
  put(endpoint, data = null, isProxy = false, headers = {}, options = {}) {
    return this._request({ endpoint, method: 'PUT', data, isProxy, headers, options });
  }

  /**
   * DELETE 请求
   */
  delete(endpoint, data = null, isProxy = false, headers = {}, options = {}) {
    return this._request({ endpoint, method: 'DELETE', data, isProxy, headers, options });
  }
}

// 实例化全局单例
const api = new ApiClient(CONFIG);

if (typeof window !== 'undefined') {
  window.api = api;
}
